/**
 * Three.js renderer for "Tvoja mapa seba". Framework-agnostic: SelfMap.vue owns the state and the
 * reading panel, this class only draws the sky, labels and tooltip and reports clicks back.
 * Loaded with a dynamic import so three.js only ships to people who open the dashboard.
 */
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { SurveyTypes } from '#shared/types/Survey'
import type { SelfMapData, SelfMapDimension, SelfMapTest } from '@/composables/sebapoznanie/useSelfMap'

export type SelfMapThemeMode = null | 'all' | string

export interface SelfMapSceneElements {
    stage: HTMLElement
    canvas: HTMLCanvasElement
    labels: HTMLElement
    tooltip: HTMLElement
}

export interface SelfMapSceneCallbacks {
    onSelect: (surveyType: SurveyTypes) => void
}

type FadeKind = 'nebula' | 'spoke' | 'test' | 'dim' | 'ghost' | 'thread' | 'comet'

interface Fade {
    mat: THREE.Material & { opacity: number }
    base: number
    kind: FadeKind
    themeIds?: string[]
}

interface PlacedTest {
    test: SelfMapTest
    pos: THREE.Vector3
}

type Pickable =
    | { type: 'test'; placed: PlacedTest }
    | { type: 'ghost'; placed: PlacedTest }
    | { type: 'dim'; placed: PlacedTest; dim: SelfMapDimension }

type LabelKind = 'core' | 'themeDim' | 'test' | 'ghost'

interface Label {
    el: HTMLElement
    pos: THREE.Vector3
    kind: LabelKind
    themeId?: string
    opacity: number
    w: number
    h: number
}

const SPACE = '#07061a'
const RADIUS = 12
const LABEL_PRIORITY: Record<LabelKind, number> = { core: 0, themeDim: 1, test: 2, ghost: 3 }

const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many)

function canvasTexture(draw: (g: CanvasRenderingContext2D, size: number) => void, size = 128) {
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const ctx = canvas.getContext('2d')
    if (ctx) draw(ctx, size)
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    return texture
}

function el(tag: string, className: string, text?: string) {
    const node = document.createElement(tag)
    node.className = className
    if (text !== undefined) node.textContent = text
    return node
}

export class SelfMapScene {
    private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    private readonly renderer: THREE.WebGLRenderer
    private readonly scene = new THREE.Scene()
    private readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 400)
    private readonly controls: OrbitControls
    private readonly textures: THREE.Texture[] = []
    private readonly fades: Fade[] = []
    private readonly pickables: THREE.Object3D[] = []
    private readonly satellites: THREE.Sprite[] = []
    private readonly centers: THREE.Sprite[] = []
    private readonly ghosts: THREE.LineSegments[] = []
    private readonly threads: Array<{ curve: THREE.CatmullRomCurve3; comets: THREE.Sprite[] }> = []
    private readonly labels: Label[] = []
    private readonly placed = new Map<SurveyTypes, PlacedTest>()
    private readonly ring: THREE.Sprite
    private readonly core: THREE.Sprite
    private readonly coreInner: THREE.Sprite
    private readonly stars: THREE.Points
    private readonly glowTex: THREE.Texture
    private readonly cleanups: Array<() => void> = []

    private themeMode: SelfMapThemeMode = null
    private selected: PlacedTest | null = null
    private hovered: THREE.Object3D | null = null
    private anim: {
        fp: THREE.Vector3
        tp: THREE.Vector3
        ft: THREE.Vector3
        tt: THREE.Vector3
        t0: number
        dur: number
    } | null = null
    private width = 1
    private height = 1
    private visible = true
    private frame = 0
    private last = performance.now()
    private tipTimer: ReturnType<typeof setTimeout> | undefined

    constructor(
        private readonly els: SelfMapSceneElements,
        private readonly data: SelfMapData,
        private readonly callbacks: SelfMapSceneCallbacks
    ) {
        // Throws without WebGL - SelfMap.vue catches it and keeps the 2D panel
        this.renderer = new THREE.WebGLRenderer({
            canvas: els.canvas,
            antialias: true,
            powerPreference: 'high-performance'
        })
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
        this.renderer.setClearColor(SPACE, 1)
        this.scene.fog = new THREE.FogExp2(SPACE, 0.011)

        this.controls = new OrbitControls(this.camera, els.canvas)
        this.controls.enableDamping = true
        this.controls.dampingFactor = 0.08
        this.controls.enablePan = false
        this.controls.minDistance = 7
        this.controls.maxDistance = 75
        this.controls.rotateSpeed = 0.7
        this.controls.autoRotate = !this.reducedMotion
        this.controls.autoRotateSpeed = 0.5
        const stopAuto = () => {
            this.controls.autoRotate = false
            this.anim = null
        }
        this.controls.addEventListener('start', stopAuto)
        this.cleanups.push(() => this.controls.removeEventListener('start', stopAuto))

        this.glowTex = this.track(
            canvasTexture((g, s) => {
                const r = s / 2
                const grd = g.createRadialGradient(r, r, 0, r, r, r)
                grd.addColorStop(0, 'rgba(255,255,255,1)')
                grd.addColorStop(0.16, 'rgba(255,255,255,0.9)')
                grd.addColorStop(0.4, 'rgba(255,255,255,0.28)')
                grd.addColorStop(0.7, 'rgba(255,255,255,0.06)')
                grd.addColorStop(1, 'rgba(255,255,255,0)')
                g.fillStyle = grd
                g.fillRect(0, 0, s, s)
            })
        )

        this.stars = this.createStars()
        this.core = this.sprite(this.glowTex, '#ffc6e6', 0.85, 7)
        this.coreInner = this.sprite(this.glowTex, '#ffffff', 1, 2.2)
        this.scene.add(this.core, this.coreInner)

        this.layoutTests()
        this.data.tests.forEach((test) => this.createTest(this.placed.get(test.surveyType) as PlacedTest))
        this.createThreads()

        this.ring = this.sprite(this.createRingTexture(), '#ffffff', 0, 5.8)
        this.ring.material.blending = THREE.NormalBlending
        this.scene.add(this.ring)

        this.createLabels()
        this.bindEvents()
        this.resize()
        this.camera.position.copy(this.overviewPosition(1.9))
        this.controls.target.set(0, 0, 0)
        this.controls.update()

        if (this.reducedMotion) {
            this.threads.forEach((thread) =>
                thread.comets.forEach((c) => c.position.copy(thread.curve.getPointAt(c.userData.offset)))
            )
        }
        this.frame = requestAnimationFrame(this.tick)
    }

    /** Ring the test; with `fly` also ease the camera to it */
    focus(surveyType: SurveyTypes | null, fly: boolean) {
        this.selected = surveyType ? this.placed.get(surveyType) || null : null
        if (!fly || !this.selected) return
        const { pos, test } = this.selected
        // Come in at an angle so the bright core is not right behind the cluster
        const u = pos.clone().normalize()
        const side = new THREE.Vector3(0, 1, 0).cross(u).normalize()
        const camPos = pos
            .clone()
            .add(u.multiplyScalar(test.completed ? 11 : 8.5))
            .add(side.multiplyScalar(test.completed ? 5 : 4))
            .add(new THREE.Vector3(0, 2.5, 0))
        this.flyTo(camPos, pos.clone())
    }

    overview() {
        const az = Math.atan2(
            this.camera.position.x - this.controls.target.x,
            this.camera.position.z - this.controls.target.z
        )
        this.flyTo(this.overviewPosition(az), new THREE.Vector3())
    }

    setThemeMode(mode: SelfMapThemeMode) {
        this.themeMode = mode
    }

    dispose() {
        cancelAnimationFrame(this.frame)
        clearTimeout(this.tipTimer)
        this.cleanups.forEach((fn) => fn())
        this.controls.dispose()
        this.scene.traverse((obj) => {
            const o = obj as THREE.Mesh
            o.geometry?.dispose()
            const mat = o.material as THREE.Material | THREE.Material[] | undefined
            if (Array.isArray(mat)) mat.forEach((x) => x.dispose())
            else mat?.dispose()
        })
        this.textures.forEach((t) => t.dispose())
        this.renderer.dispose()
        this.labels.forEach((l) => l.el.remove())
        this.els.tooltip.hidden = true
    }

    // ---------- construction ----------

    private track<T extends THREE.Texture>(texture: T): T {
        this.textures.push(texture)
        return texture
    }

    private sprite(texture: THREE.Texture, color: THREE.ColorRepresentation, opacity: number, scale: number) {
        const material = new THREE.SpriteMaterial({
            map: texture,
            color: new THREE.Color(color),
            transparent: true,
            opacity,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        })
        const sprite = new THREE.Sprite(material)
        sprite.scale.setScalar(scale)
        return sprite
    }

    private fade(mat: Fade['mat'], base: number, kind: FadeKind, themeIds?: string[]) {
        mat.opacity = base
        this.fades.push({ mat, base, kind, themeIds })
    }

    private createRingTexture() {
        return this.track(
            canvasTexture((g, s) => {
                const r = s / 2
                g.strokeStyle = 'rgba(255,255,255,0.95)'
                g.lineWidth = 2.5
                g.setLineDash([10, 7])
                g.beginPath()
                g.arc(r, r, r - 6, 0, Math.PI * 2)
                g.stroke()
                g.setLineDash([])
                g.strokeStyle = 'rgba(255,255,255,0.18)'
                g.lineWidth = 8
                g.beginPath()
                g.arc(r, r, r - 6, 0, Math.PI * 2)
                g.stroke()
            })
        )
    }

    private createStars() {
        const starTex = this.track(
            canvasTexture((g, s) => {
                const r = s / 2
                const grd = g.createRadialGradient(r, r, 0, r, r, r)
                grd.addColorStop(0, 'rgba(255,255,255,1)')
                grd.addColorStop(0.35, 'rgba(255,255,255,0.5)')
                grd.addColorStop(1, 'rgba(255,255,255,0)')
                g.fillStyle = grd
                g.fillRect(0, 0, s, s)
            }, 32)
        )
        const count = 1400
        const positions = new Float32Array(count * 3)
        const colors = new Float32Array(count * 3)
        const tints = ['#ffffff', '#dcd3ff', '#ffd9ec', '#cfefff'].map((c) => new THREE.Color(c))
        for (let i = 0; i < count; i++) {
            const u = Math.random() * 2 - 1
            const a = Math.random() * Math.PI * 2
            const r = 70 + Math.random() * 90
            const sq = Math.sqrt(1 - u * u)
            positions.set([r * sq * Math.cos(a), r * u, r * sq * Math.sin(a)], i * 3)
            const c = (tints[i % tints.length] as THREE.Color).clone().multiplyScalar(0.45 + Math.random() * 0.55)
            colors.set([c.r, c.g, c.b], i * 3)
        }
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
        const stars = new THREE.Points(
            geometry,
            new THREE.PointsMaterial({
                map: starTex,
                size: 1.1,
                sizeAttenuation: true,
                vertexColors: true,
                transparent: true,
                opacity: 0.9,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
                fog: false
            })
        )
        this.scene.add(stars)
        return stars
    }

    /** Tests sit on a sphere, each sector in its own wedge; untaken tests float a bit further out */
    private layoutTests() {
        this.data.sectors.forEach((sector, si) => {
            const angle = si * ((Math.PI * 2) / this.data.sectors.length)
            const list = this.data.tests.filter((t) => t.sectorId === sector.id)
            list.forEach((test, i) => {
                const lat = list.length === 1 ? 0 : THREE.MathUtils.degToRad(-40 + (80 * i) / (list.length - 1))
                const lon = angle + THREE.MathUtils.degToRad((i % 2 ? 1 : -1) * 13)
                const r = test.completed ? RADIUS : RADIUS + 1.2
                const pos = new THREE.Vector3(
                    r * Math.cos(lat) * Math.sin(lon),
                    r * Math.sin(lat),
                    r * Math.cos(lat) * Math.cos(lon)
                )
                this.placed.set(test.surveyType, { test, pos })
            })
            const nebula = this.sprite(this.glowTex, sector.color, 0.08, 19)
            nebula.position.set(Math.sin(angle) * 13, 0, Math.cos(angle) * 13)
            this.scene.add(nebula)
            this.fade(nebula.material, 0.08, 'nebula')
        })
    }

    private sectorColor(test: SelfMapTest) {
        return new THREE.Color(this.data.sectors.find((s) => s.id === test.sectorId)?.color || '#ffffff')
    }

    private createTest(placed: PlacedTest) {
        const { test, pos } = placed
        const color = this.sectorColor(test)
        const white = new THREE.Color('#ffffff')
        const group = new THREE.Group()
        group.position.copy(pos)
        this.scene.add(group)
        const spoke = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), pos])

        if (!test.completed) {
            const dashed = new THREE.LineDashedMaterial({
                color,
                dashSize: 0.35,
                gapSize: 0.45,
                transparent: true,
                depthWrite: false
            })
            const line = new THREE.Line(spoke, dashed)
            line.computeLineDistances()
            this.scene.add(line)
            this.fade(dashed, 0.16, 'spoke')

            const ico = new THREE.LineSegments(
                new THREE.EdgesGeometry(new THREE.IcosahedronGeometry(0.85, 0)),
                new THREE.LineBasicMaterial({ color, transparent: true, depthWrite: false })
            )
            ico.rotation.set(Math.random() * 3, Math.random() * 3, 0)
            group.add(ico)
            this.fade(ico.material, 0.5, 'ghost')
            this.ghosts.push(ico)
            const dot = this.sprite(this.glowTex, color, 0.22, 1.2)
            group.add(dot)
            this.fade(dot.material, 0.22, 'ghost')

            const hit = new THREE.Mesh(
                new THREE.SphereGeometry(1.5, 10, 8),
                new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false })
            )
            hit.userData.pick = { type: 'ghost', placed } satisfies Pickable
            group.add(hit)
            this.pickables.push(hit)
            return
        }

        const spokeMat = new THREE.LineBasicMaterial({
            color,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        })
        this.scene.add(new THREE.Line(spoke, spokeMat))
        this.fade(spokeMat, 0.2, 'spoke')

        const halo = this.sprite(this.glowTex, color, 0.2, 4.6)
        group.add(halo)
        this.fade(halo.material, 0.2, 'test')
        const center = this.sprite(this.glowTex, color.clone().lerp(white, 0.4), 1, 1.6)
        center.userData.pick = { type: 'test', placed } satisfies Pickable
        center.userData.baseScale = 1.6
        center.userData.phase = Math.random() * 6
        group.add(center)
        this.fade(center.material, 1, 'test')
        this.pickables.push(center)
        this.centers.push(center)

        // Satellites spiral around the test on the plane facing away from the core
        const u = pos.clone().normalize()
        const e1 = new THREE.Vector3(0, 1, 0).cross(u).normalize()
        const e2 = u.clone().cross(e1).normalize()
        test.dimensions.forEach((dim, i) => {
            const s = dim.value / 100
            const angle = i * 2.39996 + 0.7
            const rr = 0.75 + s * 1.85
            const lift = ((i % 3) - 1) * 0.3 * rr
            const p = e1
                .clone()
                .multiplyScalar(Math.cos(angle) * rr)
                .add(e2.clone().multiplyScalar(Math.sin(angle) * rr))
                .add(u.clone().multiplyScalar(lift))
            const base = 0.35 + 0.65 * s
            const scale = 0.32 + s * 0.95
            const sat = this.sprite(this.glowTex, color.clone().lerp(white, 0.15 + s * 0.45), base, scale)
            sat.position.copy(p)
            sat.userData.pick = { type: 'dim', placed, dim } satisfies Pickable
            sat.userData.baseScale = scale
            sat.userData.themeIds = dim.themeIds
            sat.userData.world = pos.clone().add(p)
            group.add(sat)
            this.fade(sat.material, base, 'dim', dim.themeIds)
            this.pickables.push(sat)
            this.satellites.push(sat)

            const lineMat = new THREE.LineBasicMaterial({
                color,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            })
            group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), p]), lineMat))
            this.fade(lineMat, 0.1 + 0.2 * s, 'dim', dim.themeIds)
        })
    }

    private dimensionWorld(surveyType: SurveyTypes, key: string): THREE.Vector3 | null {
        const sat = this.satellites.find((s) => {
            const pick = s.userData.pick as Pickable
            return pick.type === 'dim' && pick.placed.test.surveyType === surveyType && pick.dim.key === key
        })
        return sat ? (sat.userData.world as THREE.Vector3).clone() : null
    }

    /** Curved threads through the dimensions of each theme, ordered around the sphere */
    private createThreads() {
        this.data.themes.forEach((theme, ti) => {
            const points = theme.links
                .map((link) => {
                    const p = this.dimensionWorld(link.surveyType, link.dimensionKey)
                    const testPos = this.placed.get(link.surveyType)?.pos
                    return p && testPos ? { p, a: Math.atan2(testPos.x, testPos.z) } : null
                })
                .filter((x): x is { p: THREE.Vector3; a: number } => x !== null)
                .sort((a, b) => a.a - b.a)
            if (points.length < 2) return

            const control: THREE.Vector3[] = []
            points.forEach((q, i) => {
                control.push(q.p)
                const next = points[i + 1]
                if (next) {
                    const mid = q.p.clone().add(next.p).multiplyScalar(0.5)
                    const len = mid.length()
                    if (len < 0.01) mid.set(0, 1, 0)
                    mid.setLength(Math.max(len, 6.5))
                    mid.y += (ti - 1) * 1.6
                    control.push(mid)
                }
            })
            const curve = new THREE.CatmullRomCurve3(control, false, 'centripetal')
            const tubeMat = new THREE.MeshBasicMaterial({
                color: theme.color,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            })
            this.scene.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 260, 0.05, 6, false), tubeMat))
            this.fade(tubeMat, 0.3, 'thread', [theme.id])
            const comets = [0, 1, 2].map((k) => {
                const comet = this.sprite(this.glowTex, theme.color, 0.9, 0.95)
                comet.userData.offset = k / 3
                this.scene.add(comet)
                this.fade(comet.material, 0.9, 'comet', [theme.id])
                return comet
            })
            this.threads.push({ curve, comets })
        })
    }

    private addLabel(node: HTMLElement, kind: LabelKind, pos: THREE.Vector3, color: string, themeId?: string) {
        node.style.setProperty('--c', color)
        this.els.labels.appendChild(node)
        this.labels.push({ el: node, pos, kind, themeId, opacity: 0, w: node.offsetWidth, h: node.offsetHeight })
    }

    private createLabels() {
        for (const { test, pos } of this.placed.values()) {
            const color = this.sectorColor(test).getStyle()
            const node = el('div', `self-map-label ${test.completed ? 'is-done' : 'is-ghost'}`)
            node.append(el('span', 'self-map-label__title', test.title))
            const top = test.dimensions[0]
            node.append(
                el('span', 'self-map-label__detail', top ? `${top.label} ${top.display}` : `+${test.durationMin} min`)
            )
            const offset = new THREE.Vector3(0, test.completed ? -2.9 : -1.5, 0)
            this.addLabel(node, test.completed ? 'test' : 'ghost', pos.clone().add(offset), color)
        }
        for (const theme of this.data.themes) {
            for (const link of theme.links) {
                const p = this.dimensionWorld(link.surveyType, link.dimensionKey)
                if (!p) continue
                const node = el('div', 'self-map-label is-theme', `${link.label} ${link.display}`)
                this.addLabel(node, 'themeDim', p.add(new THREE.Vector3(0, 0.9, 0)), theme.color, theme.id)
            }
        }
        this.addLabel(el('div', 'self-map-label is-core', 'Ty'), 'core', new THREE.Vector3(0, -2.4, 0), '#ffffff')
    }

    // ---------- interaction ----------

    private bindEvents() {
        const { canvas, stage } = this.els
        const listen = <K extends keyof HTMLElementEventMap>(
            target: HTMLElement | Document,
            type: K,
            fn: (ev: HTMLElementEventMap[K]) => void
        ) => {
            target.addEventListener(type, fn as EventListener)
            this.cleanups.push(() => target.removeEventListener(type, fn as EventListener))
        }

        const resizeObserver = new ResizeObserver(() => this.resize())
        resizeObserver.observe(stage)
        const intersection = new IntersectionObserver(
            (entries) => {
                this.visible = Boolean(entries[0]?.isIntersecting)
            },
            { threshold: 0.01 }
        )
        intersection.observe(stage)
        this.cleanups.push(
            () => resizeObserver.disconnect(),
            () => intersection.disconnect()
        )

        listen(canvas, 'pointermove', (ev) => {
            if (ev.pointerType !== 'mouse' || ev.buttons) return
            this.hovered = this.pick(ev)
            canvas.classList.toggle('is-pointing', Boolean(this.hovered))
            if (this.hovered) this.showTip(this.hovered, ev)
            else this.hideTip()
        })
        listen(canvas, 'pointerleave', () => {
            this.hovered = null
            this.hideTip()
            canvas.classList.remove('is-pointing')
        })
        let down: { x: number; y: number } | null = null
        listen(canvas, 'pointerdown', (ev) => {
            down = { x: ev.clientX, y: ev.clientY }
            if (ev.pointerType !== 'mouse') this.hideTip()
        })
        listen(canvas, 'pointerup', (ev) => {
            if (!down) return
            const moved = Math.hypot(ev.clientX - down.x, ev.clientY - down.y)
            down = null
            if (moved > 6) return
            const obj = this.pick(ev)
            if (!obj) return
            this.callbacks.onSelect((obj.userData.pick as Pickable).placed.test.surveyType)
            if (ev.pointerType !== 'mouse') {
                this.showTip(obj, ev)
                clearTimeout(this.tipTimer)
                this.tipTimer = setTimeout(() => this.hideTip(), 3200)
            }
        })
    }

    private pick(ev: PointerEvent): THREE.Object3D | null {
        const rect = this.els.canvas.getBoundingClientRect()
        const ndc = new THREE.Vector2(
            ((ev.clientX - rect.left) / rect.width) * 2 - 1,
            -((ev.clientY - rect.top) / rect.height) * 2 + 1
        )
        const ray = new THREE.Raycaster()
        ray.setFromCamera(ndc, this.camera)
        return ray.intersectObjects(this.pickables, false)[0]?.object || null
    }

    private showTip(obj: THREE.Object3D, ev: PointerEvent) {
        const pick = obj.userData.pick as Pickable
        const tip = this.els.tooltip
        const test = pick.placed.test
        const head = el('div', 'self-map-tip__head')
        tip.replaceChildren(head)
        if (pick.type === 'dim') {
            head.append(el('span', '', pick.dim.label), el('span', 'self-map-tip__value', pick.dim.display))
            tip.append(el('div', 'self-map-tip__sub', test.title))
        } else if (pick.type === 'test') {
            const n = test.dimensions.length
            head.append(
                el('span', '', test.title),
                el('span', 'self-map-tip__value', `${n} ${plural(n, 'rozmer', 'rozmery', 'rozmerov')}`)
            )
            tip.append(el('div', 'self-map-tip__sub', 'Klikni pre detail'))
        } else {
            head.append(el('span', '', test.title), el('span', 'self-map-tip__value', `+${test.durationMin} min`))
            tip.append(el('div', 'self-map-tip__sub', 'Zatiaľ nevyplnené'), el('p', '', test.teaser))
        }
        tip.hidden = false
        const r = this.els.stage.getBoundingClientRect()
        let x = ev.clientX - r.left + 14
        let y = ev.clientY - r.top + 14
        if (x + tip.offsetWidth > this.width - 8) x = ev.clientX - r.left - tip.offsetWidth - 14
        if (y + tip.offsetHeight > this.height - 8) y = ev.clientY - r.top - tip.offsetHeight - 14
        tip.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`
    }

    private hideTip() {
        this.els.tooltip.hidden = true
    }

    // ---------- camera ----------

    private defaultDistance() {
        return Math.max(34, 16.5 / (Math.tan(THREE.MathUtils.degToRad(25)) * this.camera.aspect))
    }

    private overviewPosition(azimuth: number) {
        const elevation = 0.22
        const d = this.defaultDistance()
        return new THREE.Vector3(
            Math.sin(azimuth) * Math.cos(elevation) * d,
            Math.sin(elevation) * d,
            Math.cos(azimuth) * Math.cos(elevation) * d
        )
    }

    private flyTo(position: THREE.Vector3, target: THREE.Vector3, dur = 1150) {
        this.controls.autoRotate = false
        if (this.reducedMotion) {
            this.camera.position.copy(position)
            this.controls.target.copy(target)
            this.controls.update()
            return
        }
        this.anim = {
            fp: this.camera.position.clone(),
            tp: position.clone(),
            ft: this.controls.target.clone(),
            tt: target.clone(),
            t0: performance.now(),
            dur
        }
    }

    private resize() {
        this.width = this.els.stage.clientWidth || 1
        this.height = this.els.stage.clientHeight || 1
        this.renderer.setSize(this.width, this.height, false)
        this.camera.aspect = this.width / this.height
        this.camera.updateProjectionMatrix()
    }

    // ---------- frame ----------

    private isActive(themeId: string) {
        return this.themeMode === 'all' || this.themeMode === themeId
    }

    private fadeFactor(f: Fade) {
        if (!this.themeMode) return f.kind === 'comet' ? 0.55 : 1
        const active = (f.themeIds || []).some((id) => this.isActive(id))
        switch (f.kind) {
            case 'dim':
                return active ? 1.15 : 0.1
            case 'thread':
                return active ? 2.8 : 0.1
            case 'comet':
                return active ? 1.1 : 0.05
            case 'nebula':
                return 0.6
            case 'spoke':
                return 0.25
            default:
                return 0.3
        }
    }

    private labelFactor(label: Label) {
        if (label.kind === 'themeDim') return this.themeMode && label.themeId && this.isActive(label.themeId) ? 1 : 0
        if (label.kind === 'core') return 0.9
        if (this.themeMode) return 0.22
        return label.kind === 'ghost' ? 0.75 : 1
    }

    private readonly tick = (now: number) => {
        this.frame = requestAnimationFrame(this.tick)
        if (!this.visible || document.hidden) {
            this.last = now
            return
        }
        const dt = Math.min(0.05, (now - this.last) / 1000)
        this.last = now
        const t = now / 1000

        if (this.anim) {
            const p = Math.min(1, (now - this.anim.t0) / this.anim.dur)
            const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2
            this.camera.position.lerpVectors(this.anim.fp, this.anim.tp, e)
            this.controls.target.lerpVectors(this.anim.ft, this.anim.tt, e)
            if (p >= 1) this.anim = null
        }
        this.controls.update()

        // Zoomed in on a cluster: dim the core so it doesn't drown the dimensions
        const near = THREE.MathUtils.smoothstep(this.camera.position.distanceTo(this.controls.target), 16, 30)
        this.core.material.opacity += (0.85 * (0.2 + 0.8 * near) - this.core.material.opacity) * Math.min(1, dt * 6)
        this.coreInner.material.opacity += (0.3 + 0.7 * near - this.coreInner.material.opacity) * Math.min(1, dt * 6)

        if (!this.reducedMotion) {
            this.core.scale.setScalar(7 * (1 + 0.06 * Math.sin(t * 1.4)))
            this.stars.rotation.y += dt * 0.004
            this.ghosts.forEach((g) => {
                g.rotation.x += dt * 0.18
                g.rotation.y += dt * 0.25
            })
            this.threads.forEach((thread) =>
                thread.comets.forEach((c) =>
                    c.position.copy(thread.curve.getPointAt((t * 0.045 + c.userData.offset) % 1))
                )
            )
        }

        const k = Math.min(1, dt * 6)
        for (const f of this.fades) f.mat.opacity += (f.base * this.fadeFactor(f) - f.mat.opacity) * k

        for (const sat of this.satellites) {
            const themed = this.themeMode && (sat.userData.themeIds as string[]).some((id) => this.isActive(id))
            const target = sat.userData.baseScale * (themed ? 1.3 : 1) * (this.hovered === sat ? 1.4 : 1)
            sat.scale.setScalar(sat.scale.x + (target - sat.scale.x) * k)
        }
        for (const c of this.centers) {
            const pulse = this.reducedMotion ? 1 : 1 + 0.07 * Math.sin(t * 2 + c.userData.phase)
            c.scale.setScalar(c.userData.baseScale * pulse * (this.hovered === c ? 1.3 : 1))
        }

        if (this.selected) {
            this.ring.position.copy(this.selected.pos)
            this.ring.material.opacity += ((this.themeMode ? 0.25 : 0.85) - this.ring.material.opacity) * k
            const size = this.selected.test.completed ? 5.8 : 3.6
            this.ring.scale.setScalar(size * (this.reducedMotion ? 1 : 1 + 0.03 * Math.sin(t * 2.2)))
            if (!this.reducedMotion) this.ring.material.rotation += dt * 0.25
        } else {
            this.ring.material.opacity += (0 - this.ring.material.opacity) * k
        }

        this.updateLabels(k)
        this.renderer.render(this.scene, this.camera)
    }

    /** Project labels to screen; lower-priority labels give way where they would overlap */
    private updateLabels(k: number) {
        const camDir = this.camera.position.clone().sub(this.controls.target).normalize()
        const v = new THREE.Vector3()
        const taken: Array<{ x: number; y: number; w: number; h: number }> = []
        const order = [...this.labels].sort((a, b) => LABEL_PRIORITY[a.kind] - LABEL_PRIORITY[b.kind])

        for (const label of order) {
            v.copy(label.pos).project(this.camera)
            let target = 0
            if (v.z < 1 && Math.abs(v.x) < 1.2 && Math.abs(v.y) < 1.2) {
                const facing = label.kind === 'core' ? 1 : label.pos.clone().normalize().dot(camDir)
                target = (0.15 + 0.85 * THREE.MathUtils.smoothstep(facing, -0.35, 0.45)) * this.labelFactor(label)
                const x = (v.x * 0.5 + 0.5) * this.width
                const y = (-v.y * 0.5 + 0.5) * this.height
                const box = { x: x - label.w / 2, y: y - label.h / 2, w: label.w, h: label.h }
                if (target > 0.05) {
                    const clash = taken.some(
                        (o) => box.x < o.x + o.w && box.x + box.w > o.x && box.y < o.y + o.h && box.y + box.h > o.y
                    )
                    if (clash && label.kind !== 'core') target = 0
                    else taken.push(box)
                }
                label.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`
            }
            label.opacity += (target - label.opacity) * k
            label.el.style.opacity = label.opacity.toFixed(3)
        }
    }
}
